import React from 'react';
import { motion } from 'framer-motion';
import SEO from '@/components/SEO';
import StructuredData from '@/components/StructuredData';
import HeroSection from '@/components/HeroSection';
import SectionHeading from '@/components/SectionHeading';
import CTASection from '@/components/CTASection';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Badge } from '@/components/ui/badge';
import { FileCheck, Shield, Users, Award, BookOpen, CheckCircle2 } from 'lucide-react';

const Advisory = () => {
  const services = [
    {
      icon: FileCheck,
      title: 'Security Assessment & VAPT',
      description: 'Comprehensive vulnerability assessments and penetration testing to identify security gaps.',
      details: [
        'Network and application security testing',
        'Social engineering assessments',
        'Physical security reviews',
        'Remediation guidance and retesting'
      ]
    },
    {
      icon: Shield,
      title: 'Cyber Security Audit (CSA)',
      description: 'In-depth security audits to evaluate your overall security posture.',
      details: [
        'Security policy and procedure review',
        'Technical controls assessment',
        'Gap analysis against industry standards',
        'Executive summary and recommendations'
      ]
    },
    {
      icon: Users,
      title: 'Cyber Security Training (CST)',
      description: 'Build a security-aware culture with comprehensive training programs.',
      details: [
        'Security awareness training for employees',
        'Technical training for security teams',
        'Phishing simulation campaigns',
        'Custom training modules for your industry'
      ]
    },
    {
      icon: Award,
      title: 'ISO 27001 Certification',
      description: 'Achieve and maintain ISO 27001 compliance with expert guidance.',
      details: [
        'Gap analysis and readiness assessment',
        'ISMS implementation support',
        'Internal audit preparation',
        'Certification audit support'
      ]
    }
  ];

  const frameworks = [
    {
      name: 'SWIFT CSP',
      description: 'Customer Security Programme compliance for financial institutions',
      controls: 'All mandatory and advisory security controls'
    },
    {
      name: 'GDPR',
      description: 'General Data Protection Regulation for European data privacy',
      controls: 'Data protection, consent management, breach notification'
    },
    {
      name: 'PCI DSS',
      description: 'Payment Card Industry Data Security Standard',
      controls: '12 requirements for secure payment processing'
    },
    {
      name: 'Saudi PDPL',
      description: 'Saudi Arabia Personal Data Protection Law',
      controls: 'Data classification, consent, cross-border transfer'
    },
    {
      name: 'NCA ECC',
      description: 'Saudi National Cybersecurity Authority Essential Cybersecurity Controls',
      controls: '114 cybersecurity controls across 5 domains'
    },
    {
      name: 'NIST CSF',
      description: 'National Institute of Standards and Technology Cybersecurity Framework',
      controls: 'Identify, Protect, Detect, Respond, Recover'
    }
  ];

  const faqs = [
    {
      question: 'How long does a security assessment take?',
      answer: 'The duration depends on the scope and complexity of your environment. A typical assessment ranges from 2-6 weeks, including planning, testing, analysis, and reporting phases.'
    },
    {
      question: 'What is the difference between VAPT and a security audit?',
      answer: 'VAPT (Vulnerability Assessment and Penetration Testing) focuses on identifying and exploiting technical vulnerabilities. A security audit evaluates your overall security posture including policies, procedures, and controls against compliance standards.'
    },
    {
      question: 'Do you provide remediation support after assessments?',
      answer: 'Yes, we provide detailed remediation guidance and can assist with implementing security improvements. We also offer retesting services to verify that vulnerabilities have been properly addressed.'
    },
    {
      question: 'How often should we conduct security assessments?',
      answer: 'We recommend annual comprehensive assessments, with quarterly scans for critical assets. Additionally, assessments should be performed after significant changes to your infrastructure or applications.'
    },
    {
      question: 'Can you help us achieve multiple compliance frameworks simultaneously?',
      answer: 'Absolutely. Many frameworks have overlapping requirements. We design integrated compliance programs that efficiently address multiple standards, reducing duplication and cost.'
    },
    {
      question: 'What is included in your security training programs?',
      answer: 'Our training programs are customized to your needs and can include general security awareness, role-specific training, compliance training, technical security training, and simulated phishing campaigns.'
    }
  ];

  return (
    <>
      <SEO
        title="Cybersecurity Advisory & Compliance - VAPT, ISO 27001, SWIFT CSP | Gulf Digital"
        description="Expert cybersecurity advisory services including security assessments, VAPT, compliance consulting for SWIFT CSP, GDPR, PCI DSS, Saudi PDPL, and ISO 27001 certification."
        url="/advisory"
        ogTitle="Cybersecurity Advisory Services in Saudi Arabia | Gulf Digital"
        ogDescription="Gulf Digital delivers Cybersecurity services in Saudi Arabia through expert advisory covering VAPT, ISO 27001, SWIFT CSP, GDPR, PCI DSS, and Saudi PDPL compliance consulting."
        twitterTitle="Cybersecurity Advisory | Cybersecurity Solutions in Saudi Arabia"
        twitterDescription="Expert-led Cybersecurity solutions in Saudi Arabia for VAPT, compliance consulting, and ISO 27001 certification support."
      />
      <StructuredData type="Service" data={{
        name: 'Cybersecurity Advisory Services',
        description: 'Comprehensive advisory and compliance services including VAPT, security audits, compliance consulting, and ISO 27001 certification support'
      }} />

      <HeroSection
        eyebrow="Advisory & Compliance"
        title="Expert Guidance for Security & Compliance"
        subtitle="Navigate complex security and regulatory requirements with confidence. Our expert advisory services help you build robust security programs and achieve compliance."
        primaryCTA={{ text: 'Book a Consultation', href: '/contact' }}
        secondaryCTA={{ text: 'View Services', href: '/services' }}
      />

      {/* Advisory Services */}
      <section className="py-20 bg-white" data-testid="advisory-services-section">
        <div className="container-custom">
          <SectionHeading
            eyebrow="Advisory Services"
            title="Comprehensive Security Advisory"
            titleColor='foreground'
            description="From assessments to training, we provide the expertise you need to strengthen your security posture."
          />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="p-8 rounded-2xl bg-warm-gray-50 hover:shadow-soft-lg transition-all duration-300"
              >
                <div className="w-14 h-14 rounded-xl bg-brand-maroon/10 flex items-center justify-center mb-6">
                  <service.icon className="w-7 h-7 text-brand-maroon" />
                </div>
                <h3 className="text-2xl font-bold mb-3">{service.title}</h3>
                <p className="text-muted-foreground mb-4">{service.description}</p>
                <ul className="space-y-2">
                  {service.details.map((detail, idx) => (
                    <li key={idx} className="flex items-start space-x-2 text-sm">
                      <CheckCircle2 className="w-4 h-4 text-brand-maroon flex-shrink-0 mt-0.5" />
                      <span className="text-muted-foreground">{detail}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 bg-warm-gray-50" data-testid="process-section">
        <div className="container-custom">
          <SectionHeading
            eyebrow="Our Process"
            title="How We Guide You to Compliance"
            titleColor='foreground'
            description="A proven methodology to achieve and maintain regulatory compliance."
          />
          <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
            {[
              { step: '1', title: 'Gap Analysis', description: 'Assess current state vs. requirements' },
              { step: '2', title: 'Roadmap', description: 'Develop prioritized action plan' },
              { step: '3', title: 'Implementation', description: 'Execute controls and processes' },
              { step: '4', title: 'Validation', description: 'Internal audit and testing' },
              { step: '5', title: 'Certification', description: 'Support for external audit' }
            ].map((phase, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="text-center"
              >
                <div className="w-16 h-16 rounded-full bg-brand-maroon text-white flex items-center justify-center mx-auto mb-4 text-2xl font-bold">
                  {phase.step}
                </div>
                <h3 className="text-lg font-bold mb-2">{phase.title}</h3>
                <p className="text-sm text-muted-foreground">{phase.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-white" data-testid="faq-section">
        <div className="container-custom">
          <SectionHeading
            eyebrow="FAQ"
            title="Frequently Asked Questions"
            titleColor='foreground'
            description="Common questions about our advisory and compliance services."
          />
          <div className="max-w-3xl mx-auto">
            <Accordion type="single" collapsible className="space-y-4">
              {faqs.map((faq, index) => (
                <AccordionItem 
                  key={index} 
                  value={`item-${index}`}
                  className="bg-warm-gray-100 rounded-xl border-2 border-warm-gray-200 px-6 mb-2"
                >
                  <AccordionTrigger className="text-left font-semibold hover:text-brand-maroon">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      <CTASection
        title="Ready to Strengthen Your Security Posture?"
        description="Schedule a consultation with our advisory experts to discuss your security and compliance needs."
        primaryCTA={{ text: 'Book a Consultation', href: '/contact' }}
      />
    </>
  );
};

export default Advisory;